/**
 * Copyright (c) 2024 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { css, StyleSheet } from 'aphrodite';
import React, { useEffect, useMemo, useState } from 'react';

import { colors } from '@blend/consumer-components';
import { Sidebar } from './Sidebar/Sidebar';
import { DocumentViewer } from './DocumentViewer/DocumentViewer';
import { DocumentViewerPageHeader } from './DocumentViewerPageHeader';
import { BlendBridge } from 'blb-types/blendBridge';
import { getDocuments, getDocumentUrl, getLoan } from 'blb-api/loans';
import { useParams, useLocation } from 'react-router-dom';
import ClosingDocument from 'blb-components/common/Documents/ClosingDocument';
import * as Sentry from '@sentry/browser';
import { Scrollable } from './Scrollable/Scrollable';
import * as docUtils from 'blb-persona/common/utils/documents';

export namespace DocumentViewerPage {
  export interface Props extends React.HTMLAttributes<HTMLDivElement> {}
}

const styles = StyleSheet.create({
  DocumentViewerPageWrapper: {
    height: '100%',
    width: '100%',
    backgroundColor: colors.clayMinus2,
    display: 'flex',
    justifyContent: 'center',
  },
  mainWrapper: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: 1440,
    width: '100%',
    position: 'relative',
    backgroundColor: colors.snowPlus1,
  },
  bodyWrapper: {
    display: 'flex',
    flexDirection: 'row',
    flexGrow: 1,
  },
  contentWrapper: {
    flexGrow: 1,
    flexDirection: 'column',
    display: 'flex',
  },
});

export const DocumentViewerPage: React.FC<DocumentViewerPage.Props> = () => {
  const [documents, setDocuments] = useState<BlendBridge.API.Document[] | undefined>();
  const [loan, setLoan] = useState<BlendBridge.API.Loan | null>(null);
  const [docIdToRefMap, setDocIdToRefMap] = useState<
    Record<string, EClose.API.DocumentReferenceResponse> | undefined
  >();
  const [activePdfUrl, setActivePdfUrl] = useState<string | undefined>();
  const { loanId } = useParams<{ loanId: string }>();
  const location = useLocation();

  // Determine if we're viewing eNotes or Sign-Ahead documents
  const isViewingENotes = useMemo(() => location.pathname.includes('/enote'), [location.pathname]);

  const simplifiedDocuments: ClosingDocument[] = useMemo(() => {
    if (!docIdToRefMap) {
      return [];
    }

    const designation = isViewingENotes ? 'NOTE' : 'SIGN_AHEAD';
    const docsByDesignation = docUtils.filterAndGroupDocsByDesignations(documents || [], docIdToRefMap, [designation]);
    const filteredDocs = docsByDesignation[designation] || [];

    // If viewing eNotes, list all documents that have a VIEW action
    if (isViewingENotes) {
      return filteredDocs
        .filter(document => Array.isArray(document.actions) && document.actions.includes('VIEW'))
        .map(document => ({
          id: document._id,
          name: document.displayFileName,
        }));
    }

    // For SIGN_AHEAD docs, show all documents that have a VIEW action
    return filteredDocs
      .filter(document => Array.isArray(document.actions) && document.actions.includes('VIEW'))
      .map(document => ({
        id: document._id,
        name: document.displayFileName,
      }));
  }, [documents, docIdToRefMap, isViewingENotes]);

  const fetchLoan = async () => {
    try {
      const { data: fetchedLoan } = await getLoan(loanId);
      setLoan(fetchedLoan);
    } catch (e) {
      setDocuments([]);
      const err = new Error(`failed to fetch loan: ${loanId}`);
      Sentry.captureException(err);
    }
  };

  const fetchDocs = async () => {
    try {
      const { data: fetchedDocs } = await getDocuments(loanId);
      setDocuments(fetchedDocs);
    } catch (e) {
      setDocuments([]);
      const err = new Error(`failed to fetch documents for loan: ${loanId}`);
      Sentry.captureException(err);
    }
  };

  const onSelectDocument = (document: ClosingDocument) => {
    if (!document) {
      setActivePdfUrl(undefined);
    } else {
      const fileUrl = getDocumentUrl(loanId, document.id);
      setActivePdfUrl(fileUrl);
    }
  };

  useEffect(() => {
    const initialize = async () => {
      await Promise.all([fetchLoan(), fetchDocs()]);
    };
    void initialize();
  }, []);

  useEffect(() => {
    const updateDocIdToRefMap = () => {
      if (loan) {
        const auditEvents = loan.closing?.auditEvents;
        const documentReferences = loan.closing?.documentReferences;
        const allClosingRefs = docUtils.getAllClosingDocumentReferences(documentReferences, auditEvents);

        const docIdToRef = docUtils.convertDocumentReferencesToDocIdToRefMap(allClosingRefs);
        setDocIdToRefMap(docIdToRef);
      } else {
        setDocIdToRefMap(undefined);
      }
    };

    updateDocIdToRefMap();
  }, [loan]);

  return (
    <div className={css(styles.DocumentViewerPageWrapper)} data-it="DocumentViewerPage">
      <div className={css(styles.mainWrapper)}>
        <DocumentViewerPageHeader />
        <div className={css(styles.bodyWrapper)}>
          <Sidebar documents={simplifiedDocuments} onSelectDocument={onSelectDocument} />
          <div className={css(styles.contentWrapper)}>
            <Scrollable>{<DocumentViewer activePdfUrl={activePdfUrl} />}</Scrollable>
          </div>
        </div>
      </div>
    </div>
  );
};
